/**
 * Time budgets for one AI Builder turn, shared so the client and the server
 * cannot drift apart. They must stay ordered:
 *
 *   MODEL_REQUEST < TURN_BUDGET < TURN_BUDGET + CLIENT_GRACE < LOCK_TTL
 *
 * The server is the component that decides a turn is over: it stops starting
 * iterations past TURN_BUDGET and always answers. CLIENT_GRACE keeps the
 * client's own timeout strictly later, so it only fires when the server never
 * answered at all (process died, socket lost). LOCK_TTL is the outermost net:
 * it releases the per-flow lock if a run neither finishes nor throws.
 */

/**
 * Single Bedrock Converse call. Without it the AWS SDK waits forever. A
 * timeout is never retried: it ends the turn on the technical-issue message.
 * BUILD's planning call, thinking first, can take close to 60 seconds.
 */
export const AI_BUILDER_MODEL_REQUEST_TIMEOUT_MS = 90_000

/**
 * Wall clock after which the agent loop stops starting new iterations. With
 * BUILD thinking on, a multi-step build can take over two minutes.
 */
export const AI_BUILDER_TURN_BUDGET_MS = 180_000

/** Extra time the client waits beyond the server budget before giving up. */
export const AI_BUILDER_CLIENT_GRACE_MS = 120_000

/** Client-side ceiling: the server should always answer before this. */
export const AI_BUILDER_CLIENT_TIMEOUT_MS =
  AI_BUILDER_TURN_BUDGET_MS + AI_BUILDER_CLIENT_GRACE_MS

/**
 * Per-flow lock lifetime. Longer than the client ceiling: an in-flight run
 * must never lose its lock to expiry, or two agents would write the same flow.
 */
export const AI_BUILDER_FLOW_LOCK_TTL_MS = AI_BUILDER_CLIENT_TIMEOUT_MS + 60_000

/**
 * Keyed on the flow alone — the invariant is "one agent run per flow", and flow
 * ids are already unique. Any extra component would add no uniqueness while
 * adding a way for two requests on the same flow to build different keys.
 * Callers must verify the flow belongs to the requester before locking it.
 */
export const aiBuilderFlowLockKey = (flowId: string): string =>
  `ai-builder:flow:${flowId}`
